/**
 * Permission codes the Financial Structures screens (FSD 1.2.7 "Ledgers" and
 * "Accounting Calendars") declare an interest in.
 *
 * Every string is the exact dot-string the server registers with the RBAC
 * catalogue and gates the route on (`server/internal/org/handler_ledgerdepth.go`):
 *
 *  - `ledgerRead`/`ledgerManage` — `org/permissions.go` `PermLedgerRead`,
 *    `org/ledgerdepth.go` `PermLedgerManage`. Read gates the ledger list and the
 *    Ledger Selector (`/foundation/ledgers/resolve`).
 *  - `reportingCurrencyRead`/`Manage` — `org/reportingcurrency.go`. Listing and
 *    adding a ledger's reporting currencies are their own slice, not `ledgerManage`.
 *  - `ledgerSetRead`/`Manage` — `org/ledgerset.go`.
 *  - `accountingMethodRead`/`Manage` — `org/accountingmethod.go`.
 *  - `fiscalRead`/`fiscalManage` — `org/permissions.go` `PermFiscalRead`/`Manage`;
 *    `/foundation/calendars` (list + create) and `/fiscal-periods` (the period
 *    timeline) are both gated on them.
 *
 * The last block are the pickers' own read permissions (the endpoints that feed a
 * select belong to other modules): without them the form falls back to a raw id
 * input rather than an empty picker, the same honest fallback the divisions form
 * uses for its owner field.
 */

export const LEDGER_PERMISSIONS = {
  ledgerRead: 'org.ledger.read',
  ledgerManage: 'org.ledger.manage',

  reportingCurrencyRead: 'org.reporting_currency.read',
  reportingCurrencyManage: 'org.reporting_currency.manage',

  ledgerSetRead: 'org.ledger_set.read',
  ledgerSetManage: 'org.ledger_set.manage',

  accountingMethodRead: 'org.accounting_method.read',
  accountingMethodManage: 'org.accounting_method.manage',

  fiscalRead: 'org.fiscal.read',
  fiscalManage: 'org.fiscal.manage',

  /** Picker feeds owned by other modules. */
  companyRead: 'org.company.read',
  coaStructureRead: 'coa.structure.read',
  currencyRead: 'fx.currency.read',
} as const

export type LedgerPermission = (typeof LEDGER_PERMISSIONS)[keyof typeof LEDGER_PERMISSIONS]
